/**
 * <LineItemsView /> — collapsible receipt-style breakdown.
 *
 * Drop under any list row (movement / payment / receivable) when the doc
 * has a `line_items` array. Shows total computed from items, a mismatch
 * warning when the parent `amount` doesn't match, and the per-line detail
 * inside a chevron-toggled drawer.
 *
 * Pure-display: never mutates the parent doc.
 */
import { useState } from "react";
import { ChevronDown, ChevronRight, ReceiptText, AlertTriangle, CheckCircle2 } from "lucide-react";
import { formatMoney } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { tx } from "../i18n";

export function LineItemsView({ items, amount, currency, testid = "line-items", reconciled = false, claimedAmount = null }) {
  const { lang } = useLang();
  const [open, setOpen] = useState(false);

  if (!Array.isArray(items) || items.length === 0) return null;

  const sum = items.reduce((acc, it) => {
    const sub = it.subtotal != null
      ? Number(it.subtotal)
      : Number(it.qty || 1) * Number(it.price || 0);
    return acc + (Number.isFinite(sub) ? sub : 0);
  }, 0);

  const parentAmount = Number(amount || 0);
  // Treat anything ≤ 1 currency unit OR < 0.5% relative drift as "matches".
  const diff = parentAmount - sum;
  const rel = parentAmount ? Math.abs(diff) / parentAmount : 0;
  const matches = Math.abs(diff) <= 1 || rel < 0.005;

  // iter-169zj — when the backend reconciled the OCR amount against
  // the line totals, surface a calm green banner so the user knows we
  // caught the IA's hallucination and the total they see is the correct one.
  const claimed = Number(claimedAmount || 0);

  return (
    <div className="mt-2 w-full" data-testid={testid}>
      {reconciled && claimed > 0 && Math.abs(claimed - parentAmount) > 0.5 && (
        <div
          data-testid={`${testid}-reconciled-banner`}
          className="mb-2 flex items-start gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-3 py-2 text-[12px] leading-snug text-emerald-900"
        >
          <CheckCircle2 className="h-4 w-4 text-emerald-600 shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold">
              {tx(lang, "Total reconciled with line item sum", "Total reconciliado con la suma de renglones", "Total reconciliado con la suma de renglones")}
            </div>
            <div className="text-emerald-800/80 text-[11px] mt-0.5 tabular-nums">
              {tx(lang, "AI detected:", "Detectado por IA:", "Detectado por IA:")}{" "}
              <span className="line-through">{formatMoney(claimed, currency)}</span>
              {" · "}
              {tx(lang, "Actual:", "Real:", "Real:")}{" "}
              <span className="font-bold">{formatMoney(parentAmount, currency)}</span>
            </div>
          </div>
        </div>
      )}
      <button /* design-system: ALLOW legacy-todo */
        type="button"
        onClick={() => setOpen((v) => !v)}
        data-testid={`${testid}-toggle`}
        className="inline-flex items-center gap-1.5 text-[11px] font-medium text-slate-600 hover:text-emerald-700 transition-colors rounded-md px-2 py-1 -mx-2 hover:bg-emerald-50"
      >
        {open ? <ChevronDown className="h-3.5 w-3.5" /> : <ChevronRight className="h-3.5 w-3.5" />}
        <ReceiptText className="h-3.5 w-3.5 text-emerald-600" />
        <span className="tabular-nums">
          {items.length} {tx(lang,
            items.length === 1 ? "item" : "items",
            items.length === 1 ? "ítem" : "ítems",
            items.length === 1 ? "item" : "itens",
          )}
        </span>
        <span className="text-slate-400">·</span>
        <span className="tabular-nums font-semibold">{formatMoney(sum, currency)}</span>
        {!matches && (
          <span
            data-testid={`${testid}-mismatch-chip`}
            title={tx(lang, `Items sum (${formatMoney(sum, currency)}) differs from total (${formatMoney(parentAmount, currency)}) by ${formatMoney(Math.abs(diff), currency)}`, `La suma de ítems (${formatMoney(sum, currency)}) difiere del total (${formatMoney(parentAmount, currency)}) por ${formatMoney(Math.abs(diff), currency)}`, `La suma de ítems (${formatMoney(sum, currency)}) difiere del total (${formatMoney(parentAmount, currency)}) por ${formatMoney(Math.abs(diff), currency)}`)}
            className="ml-1 inline-flex items-center gap-1 text-amber-700 bg-amber-50 ring-1 ring-amber-200 rounded-full px-1.5 py-0.5"
          >
            <AlertTriangle className="h-3 w-3" />
            <span className="text-[10px] font-bold uppercase tracking-wider">
              {diff > 0
                ? (tx(lang, `+${formatMoney(diff, currency)}`, `+${formatMoney(diff, currency)}`, `+${formatMoney(diff, currency)}`))
                : `${formatMoney(diff, currency)}`}
            </span>
          </span>
        )}
      </button>

      {open && (
        <div
          className="mt-2 rounded-xl border border-slate-200 bg-slate-50/60 overflow-hidden"
          data-testid={`${testid}-table`}
        >
          <table className="w-full text-[12px]">
            <thead className="bg-white/80 text-slate-500 uppercase tracking-wider text-[10px]">
              <tr>
                <th className="text-left px-3 py-2 font-semibold">{tx(lang, "Qty", "Cant.", "Cant.")}</th>
                <th className="text-left px-3 py-2 font-semibold">{tx(lang, "Item", "Descripción", "Descripción")}</th>
                <th className="text-right px-3 py-2 font-semibold">{tx(lang, "Price", "Precio", "Precio")}</th>
                <th className="text-right px-3 py-2 font-semibold">{tx(lang, "Subtotal", "Subtotal", "Subtotal")}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it, idx) => {
                const qty = Number(it.qty || 1);
                const price = Number(it.price || 0);
                const sub = it.subtotal != null ? Number(it.subtotal) : qty * price;
                return (
                  <tr
                    key={idx}
                    data-testid={`${testid}-row-${idx}`}
                    className="border-t border-slate-100 hover:bg-white/60"
                  >
                    <td className="px-3 py-1.5 tabular-nums text-slate-600">
                      {qty % 1 === 0 ? qty : qty.toFixed(2)}
                    </td>
                    <td className="px-3 py-1.5 text-slate-800">{it.name}</td>
                    <td className="px-3 py-1.5 tabular-nums text-right text-slate-600">
                      {formatMoney(price, currency)}
                    </td>
                    <td className="px-3 py-1.5 tabular-nums text-right font-semibold text-slate-800">
                      {formatMoney(sub, currency)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="bg-white/80 border-t border-slate-200">
                <td colSpan={3} className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-semibold text-slate-500">
                  {tx(lang, "Items total", "Total ítems", "Total ítems")}
                </td>
                <td className="px-3 py-2 text-right tabular-nums font-display font-bold text-emerald-700">
                  {formatMoney(sum, currency)}
                </td>
              </tr>
              {!matches && (
                <tr className="bg-amber-50 border-t border-amber-200">
                  <td colSpan={3} className="px-3 py-2 text-right text-[11px] uppercase tracking-wider font-semibold text-amber-700">
                    {tx(lang, "Delta vs total", "Diferencia con total", "Diferencia con total")}
                  </td>
                  <td className="px-3 py-2 text-right tabular-nums font-bold text-amber-700">
                    {diff > 0 ? "+" : ""}{formatMoney(diff, currency)}
                  </td>
                </tr>
              )}
            </tfoot>
          </table>
        </div>
      )}
    </div>
  );
}

/**
 * Helper to derive a clean headline by stripping a trailing parenthetical
 * "list" off a free-text description. Frontend-only — does NOT modify the
 * backend doc; it's purely for nicer display.
 */
export function cleanHeadline(text) {
  if (!text || typeof text !== "string") return text || "";
  // Strip ` (item1 NNN + item2 NNN)` at the end
  const stripped = text.replace(/\s*\([^()]+\+[^()]+\)\s*$/, "").trim();
  return stripped || text;
}
